import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { initControlsState, PLAYBACK_ENUM } from "./utils/layoutPlaybackConfig";
import { useImmer } from "use-immer";
import "./layoutPlayback.scss";
import { useLocation } from "react-router-dom";
import { debounce, getQueryParams } from "./utils/playbackUtils";
import { LAYOUT_PLAYBACK_URL } from "../../../Common/EndPoints";
import { useSelector } from "react-redux";
import { selectedCustomersSelector } from "../../../features/authentication/auth-slice";
import PlaybackSvgPopulator from "./playback-compoents/PlaybackSvgPopulator";
import PlaybackControls from "./playback-compoents/PlaybackControls";
import { SELECTED_CUSTOMER_SVG } from "../UTILS/Constant/Constant";
import BufferingLoader from "./playback-compoents/BufferingLoader";
import { v4 as uuid } from "uuid";
import SimulationReadyPop from "./Popup/SimulationReadyPop";
import { speed, valHooks } from "jquery";
import moment from "moment";
import ReactDOMServer from "react-dom/server";

export const PlaybackContext = createContext();

const getSeekFromTs = (start_ts, end_ts, curr_ts) => {
  if (curr_ts < start_ts) return 0;
  if (curr_ts > end_ts) return 1;

  return (curr_ts - start_ts) / (end_ts - start_ts);
};

function LayoutPlayback({ queryParams, SVG }) {
  const [svgRenderKey, setSvgRenderKey] = useState(uuid());
  const { from_ts, to_ts, station } = queryParams;
  const [controls, setControls] = useImmer(initControlsState);
  const [bufferState, setBufferState] = useImmer(null);
  const [serverClock, setServerClock] = useState(null);
  const [simulationReady, setSimulationReady] = useState(false);
  const copyRef = useRef(SVG);
  const renderSvg = useMemo(() => {
    return <PlaybackSvgPopulator workingSVG={copyRef.current} key={svgRenderKey} />;
  }, [svgRenderKey]);

  //REFS
  const connectionIdRef = useRef(null);
  const playbackSocket = useRef(null);
  const yardDataRef = useRef(new Map());

  useEffect(() => {
    if (LAYOUT_PLAYBACK_URL && !playbackSocket.current) {
      let URL = `${LAYOUT_PLAYBACK_URL}?customer_id=${station.customerId}&from_ts=${from_ts}&to_ts=${to_ts}`;
      connectPlaybackSocket(URL, playbackSocket, handleSocketMessage);
    }

    return () => {
      if (playbackSocket.current) {
        playbackSocket.current.close();
      }
    };
  }, []);

  const connectPlaybackSocket = (url, socketRef, msgHandler) => {
    socketRef.current = new WebSocket(url);
    socketRef.current.onopen = () => {};
    socketRef.current.onmessage = (res) => {
      const parsed_message = JSON.parse(res.data);
      msgHandler(parsed_message);
    };
    socketRef.current.onclose = (error) => {};
  };

  const handleSocketMessage = (parsed_msg) => {
    const { conn_id, buffering, health, clock, data, init_record } = parsed_msg;
    if (!connectionIdRef.current) {
      connectionIdRef.current = conn_id;
    }
    if (buffering) {
      setBufferState(buffering);
    }
    if (health) {
      if (health.code === "SIMULATION_READY") {
        setSimulationReady(true);
      }
    }
    if (clock) {
      const ts = new Date(clock).getTime();
      const _seek = getSeekFromTs(from_ts, to_ts, ts);
      updateControls("key", { key: "seek", value: _seek });
      setServerClock(ts);
    }
    if (data) {
      const { data: pacekt, device_id, ts } = data[0];
      const parentDevice = pacekt.PARENT_DEVICE;
      if (!yardDataRef.current.has(parentDevice)) {
        yardDataRef.current.set(parentDevice, { ...pacekt, ts, device_id });
      } else {
        const existing = yardDataRef.current.get(parentDevice);
        Object.assign(existing, pacekt, { ts, device_id });
      }
    }

    if (init_record) {
      const yardDataMap = new Map();
      for (const { device_id, ts, data } of init_record) {
        const parentDevice = data.PARENT_DEVICE;

        if (!yardDataMap.has(parentDevice)) {
          yardDataMap.set(parentDevice, { ...data, ts, device_id });
        } else {
          const existing = yardDataMap.get(parentDevice);
          Object.assign(existing, data, { ts, device_id });
        }
      }
      yardDataRef.current = yardDataMap;
    }
  };
  const resetSVG = () => {
    yardDataRef.current = new Map();
    setSvgRenderKey(uuid());
  };
  const updateControls = (type, payload) => {
    if (type === "key") {
      const { key, value } = payload;
      setControls((prev) => {
        prev[key] = value;
      });
    } else if (type === "whole_state") {
      setControls(payload);
    }
  };

  const start_init = () => {
    const start_payload = {
      seek: controls.seek,
      speed: controls.speed,
    };
    updateControls("key", { key: "play", value: PLAYBACK_ENUM.PLAYING });
    sendMsgToPlaybackSocket(start_payload);
  };

  const handlePlayPause = (payload) => {
    sendMsgToPlaybackSocket(payload);
  };
  const handleRestartClick = (payload) => {
    resetSVG();
    updateControls("key", { key: "seek", value: 0 });
    sendMsgToPlaybackSocket({ seek: 0, speed: controls.speed });
  };
  const handleSeekSpeedChange = (payload) => {
    sendMsgToPlaybackSocket(payload);
  };
  const sendMsgToPlaybackSocket = (payload) => {
    playbackSocket.current.send(JSON.stringify({ ...payload, ReqId: uuid() }));
  };

  return (
    <PlaybackContext.Provider
      value={{
        controls,
        yardData: Object.fromEntries(yardDataRef.current),
        updateControls,
        queryParams,
        bufferState,
        start_init,
        serverClock,
        handlePlayPause,
        handleSeekSpeedChange,
        handleRestartClick,
        resetSVG,
      }}
    >
      <div className="layout-playback-comp">
        {renderSvg}
        <PlaybackControls />
        <BufferingLoader />
        {simulationReady && (
          <SimulationReadyPop
            open={simulationReady}
            handleClose={() => {
              setSimulationReady(false);
            }}
          />
        )}
      </div>
    </PlaybackContext.Provider>
  );
}

const InvalidParamsComp = ({ queryParams }) => {
  return <div className="invalid-params-layout-playback">INVALID PARAMS</div>;
};

const LayoutPlaybackWrapper = () => {
  const location_details = useLocation();
  const queryParams = getQueryParams(location_details.search);
  const customerIdList = useSelector(selectedCustomersSelector);
  const station = customerIdList.find((customer) => customer.customerName === queryParams?.station) ?? null;
  const SVG = SELECTED_CUSTOMER_SVG[`${queryParams?.station}`] ?? null;
  if (station && queryParams?.from_ts && queryParams?.to_ts && SVG) {
    return <LayoutPlayback queryParams={{ ...queryParams, station: station }} SVG={SVG} />;
  } else {
    return <InvalidParamsComp queryParams={queryParams} />;
  }
};

export default LayoutPlaybackWrapper;
